/**
 * Fair Tracker constants — every enum, limit and default from the build
 * specification, transcribed from the prototype so the values are exact.
 *
 * Anything a screen or rule needs to choose between lives here, never inline.
 * The spec's wording is the user-facing wording: these strings are also what
 * gets persisted (decision === 'Strong'), so changing one is a data migration,
 * not a copy edit.
 */

/* ── Media limits (rules V1, N1; spec § 7) ──────────────────────────────── */
export const VIDEO_MAX_SEC = 20;
/** Rule V1 allows 2s of slack so a 20s clip isn't rejected for rounding. */
export const VIDEO_TOLERANCE_SEC = 2;
export const VIDEO_MAX_MB = 25;
export const VOICE_MAX_SEC = 90;
export const PHOTO_MAX_PX = 1280;
export const PHOTO_QUALITY = 0.82;

/* ── Tags (spec § 3.2, § 3.4) ───────────────────────────────────────────── */
export const MAIN_PHOTO_TAG = 'Main Product Photo';
export const PHOTO_TAGS = [
  MAIN_PHOTO_TAG,
  'Product',
  'Booth',
  'Business Card',
  'Document',
  'Receipt',
];
export const CONTACT_TAGS = ['Business Card', 'Photo', 'WeChat QR', 'WhatsApp QR'];
export const REVIEW_QUALITY_TAGS = ['Sample Photo', 'Test Result', 'Inspection Report', 'Spec Sheet'];
export const REVIEW_CERT_TAGS = ['Certificate'];

/* ── Product enums (spec § 3.3) ─────────────────────────────────────────── */
export const CURRENCIES = ['USD', 'CNY', 'EUR'];
export const PRICE_UNITS = ['pc', 'set', 'carton'];
export const LEN_UNITS = ['cm', 'in'];
export const WT_UNITS = ['kg', 'lb'];
export const DECISIONS = ['Strong', 'Maybe', 'No'];
export const SAMPLE_STATES = ['No', 'Available', 'Ordered', 'Received'];
export const CERT_STATES = ['Yes', 'No', 'Need to Verify'];
export const CERT_TYPES = ['UL', 'ETL', 'FCC', 'CE', 'Other'];
export const NEXT_STEPS = [
  'Get Sample',
  'Ask Freight',
  'Negotiate Again',
  'Verify Certification',
  'Follow Up',
  'Order',
  'None',
];
export const PAY_METHODS = ['Cash RMB', 'Cash USD', 'WeChat Pay', 'Alipay', 'Card', 'Bank transfer'];
export const HANDOVER = ['Take with me', 'Ship to forwarder', 'Ship to USA', 'Pick up later'];

/* ── Follow-ups (spec § 3.5) ────────────────────────────────────────────── */
export const FU_TYPES = [
  'Appointment',
  'Ask New Price',
  'Request Sample',
  'Request Certificate',
  'Request Freight Information',
  'Request Factory Information',
  'Negotiate Terms',
  'Other',
];
export const FU_STATUS = ['Pending', 'Sent', 'Waiting', 'Done'];

/* ── Fair (spec § 3.1) ──────────────────────────────────────────────────── */
/** Stored value → the label the UI shows for it. */
export const FAIR_STAGES = [
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'active', label: 'At the fair' },
  { value: 'complete', label: 'Completed' },
];
/** 1 unit → USD. Per-fair and user-editable; there is no live FX (rule FX1). */
export const DEFAULT_FX = { CNY: '0.14', EUR: '1.08' };

/* ── Calculation defaults (rules M1, E1) ────────────────────────────────── */
export const DEFAULT_LANDED_FACTOR = 1.35;
/** Margin colour thresholds: ≥40% green, ≥25% amber, else red. */
export const MARGIN_GOOD_PCT = 40;
export const MARGIN_OK_PCT = 25;
/** A quote counts as expiring within this many days (rule E1). */
export const QUOTE_EXPIRY_DAYS = 14;

/* ── Final decision (spec § 5.12) ───────────────────────────────────────── */
export const FINAL_DECISIONS = ['APPROVED', 'HOLD', 'REJECTED'];

/* ── Account-level settings (spec § 3.6) ────────────────────────────────── */
export const DEFAULT_PROFILE = {
  company: '',
  name: '',
  title: '',
  phone: '',
  whatsapp: '',
  wechat: '',
  email: '',
  website: '',
  address: '',
  port: '',
  intro: '',
  introZh: '',
  wechatQr: '',
};

export const LANGUAGES = [
  ['en', 'English', '🇺🇸'],
  ['es', 'Español', '🇲🇽'],
  ['zh', '中文', '🇨🇳'],
  ['pt', 'Português', '🇧🇷'],
];
export const THEMES = ['auto', 'light', 'dark'];

/* ── Pre-trip checklist defaults (spec § 5.10) ──────────────────────────── */
export const CHECK_DEFAULT = [
  'Buyer badge / registration confirmed',
  'Invitation letter & visa',
  'Passport + copy (photo on phone)',
  'Flights & hotel booked',
  'VPN installed and tested',
  'China SIM / eSIM',
  'RMB cash + WeChat Pay / Alipay set up',
  '200 of my own business cards',
  'Measuring tape',
  'Luggage scale',
  'Power bank + plug adapter',
  'Comfortable shoes',
  'Forwarder address in Chinese saved in app',
  'My Fair Plan filled',
  'Backup exported before leaving',
];

/* ── Questions for suppliers (spec § 5.9) ───────────────────────────────── */
/**
 * The 11 bilingual defaults, [English, 中文]. Editable, reorderable and
 * resettable from Settings. These show on the supplier card in both languages
 * regardless of the UI language — the supplier reads the Chinese, the buyer
 * reads the English.
 */
export const CARD_Q = [
  ['Is the price FOB or EXW? Which port?', '价格是FOB还是EXW？哪个港口？'],
  ['What is the MOQ?', '最小起订量是多少？'],
  ['Price at 500 / 1,000 / 2,500 pcs?', '500 / 1000 / 2500件的价格是多少？'],
  ['Payment terms? Deposit %?', '付款方式？定金比例？'],
  ['Production lead time (days)?', '生产周期多少天？'],
  ['Do you have US certifications (UL / ETL / FCC)?', '有美国认证吗（UL / ETL / FCC）？'],
  ['Can I buy a sample? Price?', '可以买样品吗？样品价格？'],
  ['Are you a factory or a trading company?', '你们是工厂还是贸易公司？'],
  ['Carton size, weight and units per carton?', '外箱尺寸、重量、每箱多少件？'],
  ['Can you ship the sample to our forwarder in China?', '可以把样品寄到我们在中国的货代吗？'],
  ['Please write your WeChat ID here:', '请在这里写下您的微信号：'],
];

/* ── Outreach templates (spec § 5.15) ───────────────────────────────────── */
/** Placeholders: {me} {company} {fair} {product} {qty} {port} */
export const OUTREACH_EN =
  'Hello, this is {me} from {company} (Houston, Texas, USA). We met at {fair}. ' +
  'We are interested in {product}. Please send: 1) formal quotation FOB {port} ' +
  'for {qty} pcs, 2) copy of your US certifications, 3) carton size/weight and lead time. ' +
  'Thank you!';

export const OUTREACH_ZH =
  '您好，我是 {company} 的 {me}（美国德州休斯顿）。我们在 {fair} 见过面，' +
  '对 {product} 很感兴趣。请发送：1）{qty} 件 FOB {port} 正式报价，2）美国认证复印件，' +
  '3）外箱尺寸/重量和交期。谢谢！';

/** Quick Entry defaults plus the account-level settings (spec § 3.6). */
export const DEFAULT_SETTINGS = {
  currency: 'USD',
  len: 'cm',
  wt: 'kg',
  landedFactor: String(DEFAULT_LANDED_FACTOR),
  profile: { ...DEFAULT_PROFILE },
  cardQuestions: CARD_Q.map(([en, zh], i) => ({ id: `q${i + 1}`, en, zh })),
  outreach: { en: OUTREACH_EN, zh: OUTREACH_ZH },
  outreachLang: 'en',
  theme: 'auto',
  language: 'en',
};
